import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const MptReportDetail: React.FC<{
  heatNumber: string;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, metadata, projectLimitFields = [], frequency, measurements = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const mptMeasurements = measurements.filter((measurement) => measurement.reportType === 'MPT');
  const [activeMptId, setActiveMptId] = useState<string | null>(null);
  const effectiveFrequency = metadata?.frequency || frequency || 'per_pipe';
  const fieldStrengthMin = getProjectLimitMin(projectLimitFields, 'mpt_field_strength_oe', 24);
  const uvIrradianceMin = getProjectLimitMin(projectLimitFields, 'mpt_uv_irradiance_uw_cm2', 1000);
  const linearLengthMax = getProjectLimitMax(projectLimitFields, 'mpt_linear_length_mm', 1.5);
  const roundedLengthMax = getProjectLimitMax(projectLimitFields, 'mpt_rounded_length_mm', 3);
  const fieldStrengthLimitText = getProjectLimitText(projectLimitFields, 'mpt_field_strength_oe', `>= ${fieldStrengthMin} Oe`);
  const uvIrradianceLimitText = getProjectLimitText(projectLimitFields, 'mpt_uv_irradiance_uw_cm2', `>= ${uvIrradianceMin} uW/cm2`);
  const linearLimitText = getProjectLimitText(projectLimitFields, 'mpt_linear_length_mm', `<= ${linearLengthMax} mm`);
  const roundedLimitText = getProjectLimitText(projectLimitFields, 'mpt_rounded_length_mm', `<= ${roundedLengthMax} mm`);
  const projectLimitRows = getProjectLimitRows(projectLimitFields, [
    ['Field strength', fieldStrengthLimitText, true],
    ['UV irradiance', uvIrradianceLimitText, true],
    ['Linear max', linearLimitText, true],
    ['Rounded max', roundedLimitText, true],
  ]);
  const getMptIndicationStatus = (type: string, lengthMm: number, fieldStrength: number, uvIrradiance: number, disposition: string) => {
    if (disposition !== 'accept') return 'FAIL';
    if (fieldStrength < fieldStrengthMin || uvIrradiance < uvIrradianceMin) return 'FAIL';
    if (type === 'linear') return lengthMm <= linearLengthMax ? 'PASS' : 'FAIL';
    return lengthMm <= roundedLengthMax ? 'PASS' : 'FAIL';
  };
  const indications = mptMeasurements.map((measurement) => {
    const type = measurement.mptType || 'rounded';
    const lengthMm = Number(measurement.mptLengthMm) || 0;
    const fieldStrength = Number(measurement.mptFieldStrengthOe) || 0;
    const uvIrradiance = Number(measurement.mptUvIrradiance) || 0;
    const disposition = measurement.mptDisposition || 'accept';
    return {
      id: measurement.mptIndicationId || measurement.id,
      end: measurement.mptEnd || 'End 1 - PE1',
      type,
      lengthMm,
      disposition,
      status: getMptIndicationStatus(type, lengthMm, fieldStrength, uvIrradiance, disposition),
      measurement,
    };
  });
  useEffect(() => {
    if (!indications.length) {
      setActiveMptId(null);
      return;
    }

    if (!activeMptId || !indications.some((indication) => indication.id === activeMptId)) {
      setActiveMptId(indications[0].id);
    }
  }, [activeMptId, indications]);
  const activeIndication = indications.find((indication) => indication.id === activeMptId) || indications[0];
  const activeMeasurement = activeIndication?.measurement;
  const fieldStrength = Number(activeMeasurement?.mptFieldStrengthOe || 0);
  const uvIrradiance = Number(activeMeasurement?.mptUvIrradiance || 0);
  const processStatus = activeMeasurement ? getMptIndicationStatus(activeMeasurement.mptType || 'rounded', Number(activeMeasurement.mptLengthMm) || 0, fieldStrength, uvIrradiance, activeMeasurement.mptDisposition || 'accept') : 'PENDING_REVIEW';
  const failCount = indications.filter((indication) => indication.status === 'FAIL').length;
  const groupedByEnd = ['End 1 - PE1', 'End 2 - PE2'].map((end) => ({
    end,
    indications: indications.filter((indication) => indication.end === end),
  }));

  const renderEndFace = (group: typeof groupedByEnd[number], index: number) => {
    const roundedCount = group.indications.filter((indication) => indication.type === 'rounded').length;
    const linearCount = group.indications.filter((indication) => indication.type === 'linear').length;

    return (
      <Card key={group.end} className="border-[#E2E5EA]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{group.end}</div>
            <StatusBadge status={group.indications.some((indication) => indication.status === 'FAIL') ? 'FAIL' : 'PASS'} />
          </div>
          <svg viewBox="0 0 220 220" className="mt-2 h-[210px] w-full">
            <circle cx="110" cy="110" r="100" fill="#F0F2F5" stroke="#A9B2BE" strokeWidth="1.5" />
            <circle cx="110" cy="110" r="78" fill="#FFFFFF" stroke="#CAD3DB" strokeWidth="1" />
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => {
              const radians = (angle * Math.PI) / 180;
              const x1 = 110 + Math.cos(radians) * 82;
              const y1 = 110 + Math.sin(radians) * 82;
              const x2 = 110 + Math.cos(radians) * 96;
              const y2 = 110 + Math.sin(radians) * 96;
              return <line key={angle} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#95A1AF" strokeWidth="1" opacity="0.55" />;
            })}
            {group.indications.map((indication, itemIndex) => {
              const angle = ((index === 0 ? 320 : 150) + itemIndex * 26) * Math.PI / 180;
              const x = 110 + Math.cos(angle) * 46;
              const y = 110 + Math.sin(angle) * 46;
              return (
                <g key={indication.id} className={indication.measurement ? 'cursor-pointer' : ''} onClick={() => indication.measurement && setActiveMptId(indication.id)}>
                  <circle cx={x} cy={y} r={activeMptId === indication.id ? '5' : '3.5'} fill={indication.status === 'FAIL' ? '#B42318' : '#1F9A3A'} stroke={activeMptId === indication.id ? '#1F5B89' : undefined} strokeWidth={activeMptId === indication.id ? '2' : undefined} />
                  <text x={x + 8} y={y - 2} fontFamily="monospace" fontSize="8" fill="#2E3440">{indication.id}</text>
                </g>
              );
            })}
            <text x="110" y="22" textAnchor="middle" fontFamily="monospace" fontSize="9" fill="#6B7280">12</text>
            <text x="200" y="115" textAnchor="middle" fontFamily="monospace" fontSize="9" fill="#6B7280">3</text>
            <text x="110" y="208" textAnchor="middle" fontFamily="monospace" fontSize="9" fill="#6B7280">6</text>
            <text x="20" y="115" textAnchor="middle" fontFamily="monospace" fontSize="9" fill="#6B7280">9</text>
          </svg>
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-heritage-grey">
            <span>{roundedCount} rounded indication</span>
            <span>-</span>
            <span>{linearCount} linear</span>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">End-face inspection</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">{metadata?.source ? formatLabel(metadata.source) : 'MPT'} · {mptMeasurements.length} saved indication{mptMeasurements.length === 1 ? '' : 's'}</div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('MPT')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Indication
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {groupedByEnd.map(renderEndFace)}
        </div>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Process parameters" />
            <TensileResultRow label="Magnetic field strength" formula={fieldStrengthLimitText} value={fieldStrength} unit="Oe" min={Math.max(0, fieldStrengthMin * 0.75)} max={Math.max(fieldStrengthMin * 1.5, fieldStrength)} lo={fieldStrengthMin} status={activeMeasurement ? (fieldStrength >= fieldStrengthMin ? 'PASS' : 'FAIL') : processStatus} />
            <TensileResultRow label="UV-A irradiance" formula={uvIrradianceLimitText} value={uvIrradiance} unit="uW/cm2" min={Math.max(0, uvIrradianceMin * 0.75)} max={Math.max(uvIrradianceMin * 1.5, uvIrradiance)} lo={uvIrradianceMin} status={activeMeasurement ? (uvIrradiance >= uvIrradianceMin ? 'PASS' : 'FAIL') : processStatus} />
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Indications" />
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">End</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">Length mm</th>
                  <th className="px-4 py-3">Disposition</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {indications.map((indication) => (
                  <tr key={indication.id} onClick={() => indication.measurement && setActiveMptId(indication.id)} className={cn(indication.measurement && 'cursor-pointer hover:bg-page-bg', activeMptId === indication.id && 'bg-[#E7F3FF]')}>
                    <td className="px-4 py-3 font-mono font-semibold">{indication.id}</td>
                    <td className="px-4 py-3">{indication.end}</td>
                    <td className="px-4 py-3">{indication.type.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-right font-mono">{indication.lengthMm.toFixed(1)}</td>
                    <td className="px-4 py-3">{indication.disposition}</td>
                    <td className="px-4 py-3"><StatusBadge status={indication.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {indication.measurement && (
                        <ItemActionButtons measurement={indication.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', metadata?.reportId || '-', true],
          ['Performed at', formatDisplayDate(metadata?.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', metadata?.lab || '-'],
          ['Technician', metadata?.technician || '-'],
          ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
          ['Source file', metadata?.sourceFile || '-', true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows rows={[['Frequency', effectiveFrequency, true], ['Indications', `${mptMeasurements.length} saved`, true], ['Rejects', failCount, true]]} />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};
